import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';

import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';

import {
    ApplicationFacade,
    BookRidePlan,
    BookRideSpecification,
    CustomerFacade,
    NotificationFacade,
    PaymentMethodsFacade,
    PurchaseFacade,
    RidePlannerFacade,
    ShoppingCartFacade,
    ShoppingCartState
} from '@ridango/components';
import { clearCartState, loadCartState } from '@ridango/components/lib/localStorage';
import { LogService } from '@ridango/core';

import { Observable } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { BaseComponent } from '@ridango/core';

@Component({
    selector: 'rwidget-payment-result',
    templateUrl: './payment-result.component.html'
})
export class PaymentResultComponent extends BaseComponent implements OnInit, OnDestroy {
    cart$: Observable<ShoppingCartState> = this.cartFacade.cart$;
    docNums$: Observable<any[]> = this.cartFacade.docNums$;
    groupedTicketsByProduct$ = this.cartFacade.groupedTicketsByProduct$;
    ticketsGrouped$: Observable<any> = this.cartFacade.ticketsGrouped$;
    assetsPath$ = this.applicationFacade.assetsPath$;

    plan$: Observable<BookRidePlan> = this.ridePlannerFacade.plan$;
    passengerPermission$: Observable<any> = this.customerFacade.passengerPermission$;
    specification$: Observable<BookRideSpecification> = this.ridePlannerFacade.specification$;
    estimatedPrice$: Observable<number> = this.ridePlannerFacade.estimatedPrice$;
    bookingStatus$: Observable<any> = this.ridePlannerFacade.bookingStatus$;
    profile$: Observable<any> = this.customerFacade.profile$;

    locale = this.translate.currentLang;
    transportDayEndPrevDate;
    result;
    resultCode: string;
    invoiceResult: string;
    decodedResult;

    ticketsGrouped: any;

    constructor(
        private route: ActivatedRoute,
        private ls: LogService,
        private router: Router,
        private notifications: NotificationFacade,
        private cartFacade: ShoppingCartFacade,
        @Inject('ENV') public env: Env,
        public translate: TranslateService,
        private paymentMethodsFacade: PaymentMethodsFacade,
        private purchaseFacade: PurchaseFacade,
        private applicationFacade: ApplicationFacade,
        private ridePlannerFacade: RidePlannerFacade,
        private customerFacade: CustomerFacade
    ) {
        super();
    }

    ngOnInit() {
        this.transportDayEndPrevDate = this.env.TRANSPORT_DAY_END_PREV_DAY ? this.env.TRANSPORT_DAY_END_PREV_DAY : null;

        this.router.events
            .pipe(takeUntil(this.componentDestroyed$))
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                if (event.url.indexOf(this.env.PAYMENT_RETURN_COMPONENT) < 0) {
                    // cleanup, user moved away
                    clearCartState();
                    const cartPaid =
                        this.result === 'S' ||
                        (this.decodedResult &&
                            (this.decodedResult.status === 'Y' || this.decodedResult.status === 'A')) ||
                        this.resultCode ||
                        this.invoiceResult;

                    if (cartPaid) {
                        this.cartFacade.deleteCart();
                        this.purchaseFacade.unsetShoppingCart();
                    }
                    this.paymentMethodsFacade.setSelectedMethod(null);
                }
            });

        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            this.result = params['result'];
            this.resultCode = params['resultCode'];
            this.invoiceResult = params['invoiceResult'];

            if (this.resultCode) {
                this.handleAdyenPayment();
            } else if (this.invoiceResult) {
                this.handleInvoicePayment(this.invoiceResult);
            } else if (this.result) {
                if (this.result === 'S') {
                    this.swishPaymentSuccess();
                } else if (this.result === 'F') {
                    this.paymentFailed();
                } else {
                    this.paymentSuccess();
                }
            } else {
                this.notifications.error(this.translate.instant('LOCAL_NOTIFICATION_PAYMENT_FAILED'));
            }
        });

        this.ticketsGrouped$.pipe(takeUntil(this.componentDestroyed$)).subscribe((tg) => (this.ticketsGrouped = tg));
    }

    swishPaymentSuccess() {
        this.cart$.pipe(take(1)).subscribe((c) => {
            if (!c.shopping_cart_id) {
                // const cart = Object.assign({}, JSON.parse(JSON.stringify(loadCartState())));
                const cart = loadCartState();
                if (!cart || !cart.docNums || cart.docNums.length === 0) {
                    this.router.navigate([this.locale, 'purchase', 'ticket']);
                } else {
                    this.cartFacade.hydrateState(cart);
                    this.cartFacade.setCartStatus('L');
                }
            } else {
                this.notifications.removeAll();
                this.cartFacade.setCartStatus('Y');
                this.notifications.success(this.translate.instant('LOCAL_NOTIFICATION_PAYMENT_SUCCEEDED'));
            }
        });
    }

    paymentSuccess() {
        try {
            const jwtHelper = new JwtHelperService();
            this.decodedResult = jwtHelper.decodeToken(this.result);
        } catch (error) {
            this.paymentFailed();
            return;
        }

        const cart = loadCartState();
        if (!cart) {
            this.paymentFailed();
            return;
        }

        this.cartFacade.hydrateState(cart);

        if (cart.shopping_cart_id) {
            this.cartFacade.setCartStatus('L');
        }

        clearCartState();

        switch (this.decodedResult.status) {
            case 'Y':
            case 'A':
                this.notifications.removeAll();
                this.cartFacade.setCartStatus('Y');
                this.notifications.success(this.translate.instant('LOCAL_NOTIFICATION_PAYMENT_SUCCEEDED'));
                break;
            case 'C':
                this.paymentFailed();
                break;
            case 'F':
                this.paymentFailed(
                    this.translate.instant(this.decodedResult.reason || 'LOCAL_NOTIFICATION_PAYMENT_FAILED')
                );
                break;
            default:
                this.paymentFailed();
        }
    }

    paymentFailed(message = this.translate.instant('LOCAL_NOTIFICATION_PAYMENT_FAILED')) {
        this.notifications.error(message, {
            showAfterRouteChange: true
        });
        this.router.navigate([this.locale, 'purchase', 'cart']);
    }

    handleAdyenPayment() {
        const loadCart = new Promise((resolve, reject) => {
            const cart = loadCartState();
            if (cart) {
                resolve(cart);
            } else {
                reject();
            }
        });

        loadCart
            .then((cart: any) => {
                this.cartFacade.hydrateState(cart);
                if (cart && cart.shopping_cart_id) {
                    this.cartFacade.setCartStatus('L');
                }
                clearCartState();
                this.cartFacade.setCartStatus('Y');
            })
            .catch((e) => {
                this.ls.log(this, 'loadcart then error', e);
            });
    }

    handleInvoicePayment(invoiceResult: string) {
        const loadCart = new Promise((resolve, reject) => {
            const cart = loadCartState();
            if (cart) {
                resolve(cart);
            } else {
                reject();
            }
        });

        if (invoiceResult === 'S' && loadCart) {
            loadCart.then((cart: any) => {
                this.cartFacade.hydrateState(cart);
                if (cart && cart.shopping_cart_id) {
                    this.cartFacade.setCartStatus('L');
                }
                clearCartState();
                this.cartFacade.setCartStatus('Y');
            });
        } else {
            this.paymentFailed();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
